import { UserRole } from "models/organization/dto/invite-to-organization.dto";
import { Navigate } from "react-router-dom";
import { landingPathForRole } from "routes/access-policy";
import { useGetMyOrganizations } from "../organization/hooks/useGetMyOrganizations";
import { resolveUserRole } from "./roles";

export default function RedirectAuthorizedUser({ user }: { user: Record<string, unknown> & { role?: UserRole } }) {
    /** Where a signed-in operator lands.
     *
     * This has to know the role for the org they are about to be dropped into,
     * and the `role` custom claim cannot answer that: it is ONE global value per
     * Firebase user, and `accept-invitation-authenticated` deliberately never
     * stamps it — so a coach invited on an existing account has no claim at all,
     * `getUserRole` fails OPEN to admin, and they were sent to `/admin/home`,
     * a page they cannot render. The layout catch-all then bounced them to
     * `/admin/calendar`. Self-correcting, but a visible wrong-page flash on
     * every sign-in for exactly the population this feature exists to serve.
     *
     * So: resolve from the same org the app is about to auto-select
     * (`organizations[0]` — see `useGetMyOrganizations` / `OrganizationPicker`,
     * which both select the head of the list) and fall back to the claim only
     * when the server has no answer.
     *
     * Rendering `null` while the query is in flight is deliberate and bounded:
     * it is one request, `AuthProvider` already withholds the whole tree during
     * credential init, and an ERROR counts as resolved (see
     * `useMyOrganizationsResolved`) so a dead API falls back to the claim
     * instead of hanging on a blank page. */
    const isSignedIn = Boolean(user);
    const { data: organizations, isPlaceholderData, isError } = useGetMyOrganizations(isSignedIn);

    // Not signed in: nothing to resolve, and `ProtectedRoute` will bounce this
    // to /auth anyway. Keep the original claim-only behaviour.
    if (isSignedIn && isPlaceholderData && !isError) return null;

    return <Navigate to={landingPathForRole(resolveUserRole(user, organizations?.[0]?.myRole))} replace />;
}
